/* eslint-disable */
import React from "react";
import { CalendarMonth } from '../../../controls/CalendarMonth';
import { CalendarHierarchy, InputCalendarHierarchyItem } from '../../../controls/CalendarHierarchy';
import { ICalendarItem, useDictionary, useParameter } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { equal } from "../../../../../shared";
import { orderBy } from "lodash";

export const CalendarHierarchyControl: React.FC = () => {
    const [controlItems, setControlItems] = React.useState<InputCalendarHierarchyItem[] | undefined>();
    const [dictItems] = useDictionary(dictionaryService, "calendar-origin");
    const [, setCalendarValue] = useParameter<number[]>("@calendar");
    const [calendarDisplayValue, setCalendarDisplayValue] = useParameter<number[]>("@calendar_display");
    const [yearFrequency, monthFrequency, weekFrequency] = [1, 4, 7];

    React.useEffect(function updateControlsItemsByDictionaryItems() {
        const candiatesControlItems = dictItems?.map<InputCalendarHierarchyItem & { frequency: number }>(item => ({
            id: Number(item.id),
            parent: item.getValue<number>('parent'),
            name: item.getValue<string>('name') || '',
            frequency: item.getValue<number>('frequencyId')!,
        }));

        const [minYear, maxYear] = [120020101, Number(`1${new Date().getFullYear()}0101`)];
        const nextControlItems = candiatesControlItems?.reduce((result, item) => {
            const itemId = item.id as number;
            if (item.frequency === yearFrequency && itemId >= minYear && itemId <= maxYear) {
                result.push(item);
            } else if (item.frequency === monthFrequency) {
                const paretQuartal = dictItems?.find(dictItem => equal(+dictItem.id, item.parent));
                const parentYearId = paretQuartal?.getValue<number>('parent')!;
                if (parentYearId >= minYear && parentYearId <= maxYear) {
                    item.parent = parentYearId;
                    result.push(item);
                }
            } else if (item.frequency === weekFrequency) {
                const paretMonth = result?.find(resultItem => equal(Number(resultItem.id), item.parent));
                if (paretMonth) {
                    result.push(item);
                }
            }
            return result;
        }, [] as typeof candiatesControlItems);

        setControlItems(nextControlItems);
    }, [dictItems]);

    const handlerOrder = (value: number[]) => {
        const primaryOrder = orderBy(value, e=> Number(e.toString().slice(0, 1)));
        const secondaryOrder = orderBy(primaryOrder, e=> Number(e.toString().slice(1)));
        return secondaryOrder;
    }

    const handlerChange = React.useCallback((value: unknown) => {
        const weeksOrMonthesOrYearsIds = handlerOrder(value as number[]);
        setCalendarDisplayValue(weeksOrMonthesOrYearsIds);

        const valueLength = weeksOrMonthesOrYearsIds.length;
        const calendarIds = weeksOrMonthesOrYearsIds.flatMap(weekOrMonthOrYearId => {
            const firstCharacter = weekOrMonthOrYearId.toString().slice(0, 1);
            const frequency = Number(firstCharacter);
            const isYear = frequency === yearFrequency;
            const isMonth = frequency === monthFrequency;
            if (isYear) {
                if (valueLength === 1) {
                    const monthes = controlItems?.filter(e => e.parent === weekOrMonthOrYearId).map(e => e.id) as number[];
                    return monthes || [];
                }
                return [weekOrMonthOrYearId];
            }
            if (isMonth) {
                if (valueLength === 1) {
                    const weeks = controlItems?.filter(e => e.parent === weekOrMonthOrYearId).map(e => e.id) as number[];
                    return weeks || [];
                }
                return [weekOrMonthOrYearId];
            }
            return [weekOrMonthOrYearId];
        });
        setCalendarValue(handlerOrder(calendarIds));
    }, [controlItems]);

    return <CalendarHierarchy selectableParent label="Calendar" multiple value={calendarDisplayValue} onChange={handlerChange} items={controlItems} />;
};
